/**
 * DeviceService for managing smart home devices
 * Integrates with the API Layer for device control and real-time updates
 */

import { Device } from '../components/DeviceCard';
import ApiService, { WebSocketMessage } from './ApiService';

// Map API device format to frontend device format
const mapApiDeviceToFrontend = (apiDevice: any): Device => {
  // Use deviceId if id is not available or vice versa
  const id = apiDevice.id || apiDevice.deviceId;
  
  // Check if the device is virtual based on id or isVirtual flag
  const isVirtual = (id && id.toString().startsWith('virtual-')) || apiDevice.isVirtual === true;
  
  // Use either room or location for the room field
  const room = apiDevice.room || apiDevice.location || 'Unknown';
  
  // Ensure state is an object
  const state = apiDevice.state || {};
  
  // Convert any string numeric values to numbers for the frontend
  Object.keys(state).forEach(key => {
    if (typeof state[key] === 'string' && !isNaN(Number(state[key]))) {
      state[key] = Number(state[key]);
    }
  });
  
  return {
    id: id,
    name: apiDevice.name || 'Unnamed Device',
    type: apiDevice.deviceType || 'switch',
    room: room,
    isVirtual: isVirtual,
    state: state
  };
};

class DeviceService {
  private devices: Device[] = [];
  private isInitialized = false;
  private isConnected = false;
  
  constructor() {
    // Set up WebSocket message handler
    ApiService.addMessageHandler(this.handleDeviceUpdate);
  }
  
  /**
   * Initialize the service by loading devices and setting up real-time updates
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    try {
      // Load devices from API
      await this.loadDevices();
      
      // Connect to WebSocket for real-time updates
      try {
        await this.connectToRealTimeUpdates();
      } catch (wsError) {
        console.warn('Could not connect to real-time updates, continuing without WebSocket:', wsError);
        // Continue without WebSocket - we'll use polling as fallback
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing DeviceService:', error);
      // Initialize with empty device list but mark as initialized to prevent repeated failures
      this.devices = [];
      this.isInitialized = true;
    }
  }
  
  /**
   * Load devices from the API
   */
  private async loadDevices(): Promise<void> {
    try {
      console.log('Loading devices from API');
      const apiDevices = await ApiService.getDevices();
      console.log('Received devices from API:', apiDevices);
      
      if (Array.isArray(apiDevices)) {
        this.devices = apiDevices.map(mapApiDeviceToFrontend);
        console.log('Mapped devices:', this.devices);
      } else {
        console.error('API did not return an array of devices:', apiDevices);
        this.devices = [];
      }
    } catch (error) {
      console.error('Error loading devices from API:', error);
      throw error;
    }
  }
  
  /**
   * Connect to WebSocket for real-time updates
   */
  private async connectToRealTimeUpdates(): Promise<void> {
    if (this.isConnected) return;
    
    try {
      await ApiService.connectWebSocket();
      
      // Subscribe to updates for all devices
      for (const device of this.devices) {
        try {
          await ApiService.subscribeToDevice(device.id);
        } catch (subError) {
          console.warn(`Could not subscribe to updates for device ${device.id}:`, subError);
          // Continue with other devices
        }
      }
      
      this.isConnected = true;
    } catch (error) {
      console.error('Error connecting to real-time updates:', error);
      // Don't throw error, just mark as not connected
      this.isConnected = false;
    }
  }
  
  /**
   * Handle device update from WebSocket
   */
  private handleDeviceUpdate = (message: WebSocketMessage): void => {
    if (message.event !== 'deviceUpdate' || !message.deviceId || !message.state) return;
    
    const deviceIndex = this.devices.findIndex(d => d.id === message.deviceId);
    if (deviceIndex === -1) return;
    
    // Update the device state
    this.devices[deviceIndex] = {
      ...this.devices[deviceIndex],
      state: {
        ...this.devices[deviceIndex].state,
        ...message.state
      }
    };
  };
  
  /**
   * Load mock devices for testing
   */
  private loadMockDevices(): void {
    // No mock devices, we'll use virtual devices instead
    this.devices = [];
  }
  
  /**
   * Get all devices
   * @returns Array of devices
   */
  async getDevices(): Promise<Device[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return [...this.devices];
  }
  
  /**
   * Get a device by ID
   * @param deviceId - The device ID
   * @returns The device or undefined if not found
   */
  async getDevice(deviceId: string): Promise<Device | undefined> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.devices.find(device => device.id === deviceId);
  }
  
  /**
   * Update a device's state
   * @param deviceId - The device ID
   * @param updates - The state updates to apply
   * @returns The updated device
   */
  async updateDevice(deviceId: string, updates: Partial<Device['state']>): Promise<Device> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    const deviceIndex = this.devices.findIndex(device => device.id === deviceId);
    
    if (deviceIndex === -1) {
      console.warn(`Device not found in local cache: ${deviceId}, attempting to fetch from API`);
      try {
        // Try to fetch the device from the API
        const apiDevice = await ApiService.getDevice(deviceId);
        if (apiDevice) {
          const device = mapApiDeviceToFrontend(apiDevice);
          this.devices.push(device);
          return this.updateDevice(deviceId, updates);
        } else {
          throw new Error(`Device not found: ${deviceId}`);
        }
      } catch (fetchError) {
        console.error(`Error fetching device ${deviceId}:`, fetchError);
        throw new Error(`Device not found: ${deviceId}`);
      }
    }
    
    try {
      console.log(`Updating device ${deviceId} with state:`, updates);
      
      // Send the update to the API
      const result = await ApiService.controlDevice(deviceId, updates);
      
      if (!result) {
        console.warn(`API returned null result for device update ${deviceId}`);
      }
      
      // Update will come through WebSocket, but update local state immediately for responsiveness
      const updatedDevice = {
        ...this.devices[deviceIndex],
        state: {
          ...this.devices[deviceIndex].state,
          ...updates
        }
      };
      
      // Update the devices array
      this.devices[deviceIndex] = updatedDevice;
      
      // Force a refresh of the device from the API to ensure consistency
      setTimeout(async () => {
        try {
          await this.loadDevices();
        } catch (error) {
          console.error('Error refreshing devices after update:', error);
        }
      }, 1000);
      
      return updatedDevice;
    } catch (error) {
      console.error(`Error updating device ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * Find devices by name or room (case-insensitive partial match)
   * @param query - The search query
   * @returns Array of matching devices
   */
  async findDevices(query: string): Promise<Device[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    const lowerQuery = query.toLowerCase();
    
    return this.devices.filter(device => {
      return (
        device.name.toLowerCase().includes(lowerQuery) ||
        device.room.toLowerCase().includes(lowerQuery)
      );
    });
  }
  
  /**
   * Toggle a device's power state
   * @param deviceId - The device ID
   * @returns The updated device
   */
  async toggleDevice(deviceId: string): Promise<Device> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    const device = await this.getDevice(deviceId);
    
    if (!device) {
      throw new Error(`Device not found: ${deviceId}`);
    }
    
    const newPowerState = device.state.power === 'on' ? 'off' : 'on';
    
    return this.updateDevice(deviceId, { power: newPowerState });
  }
  
  /**
   * Process a command for a device
   * @param deviceName - The device name or room
   * @param action - The action to perform (e.g., 'turn on', 'turn off')
   * @param value - Optional value for the action (e.g., brightness level)
   * @returns Result of the command
   */
  async processCommand(deviceName: string, action: string, value?: number): Promise<{ success: boolean; message: string }> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    try {
      console.log(`Processing command: ${action} for device "${deviceName}"`);
      
      // Force a refresh of devices to ensure we have the latest data
      await this.loadDevices();
      
      // Find matching devices
      const matchingDevices = await this.findDevices(deviceName);
      console.log(`Found ${matchingDevices.length} matching devices:`, matchingDevices);
      
      if (matchingDevices.length === 0) {
        console.warn(`No devices found matching "${deviceName}"`);
        return {
          success: false,
          message: `No devices found matching "${deviceName}"`
        };
      }
      
      // Process the action for each matching device
      const results = await Promise.all(matchingDevices.map(async device => {
        const lowerAction = action.toLowerCase();
        console.log(`Processing ${lowerAction} for device ${device.id} (${device.name})`);
        
        if (lowerAction === 'on' || lowerAction === 'turn on') {
          // Call API directly to ensure the command goes through
          try {
            const result = await ApiService.controlDevice(device.id, { power: 'on' });
            console.log(`Direct API call result for turning on ${device.id}:`, result);
            
            // Update local state
            await this.updateDevice(device.id, { power: 'on' });
            return `Turned on ${device.name}`;
          } catch (error) {
            console.error(`Error turning on device ${device.id}:`, error);
            return `Error turning on ${device.name}`;
          }
        }
        
        if (lowerAction === 'off' || lowerAction === 'turn off') {
          // Call API directly to ensure the command goes through
          try {
            const result = await ApiService.controlDevice(device.id, { power: 'off' });
            console.log(`Direct API call result for turning off ${device.id}:`, result);
            
            // Update local state
            await this.updateDevice(device.id, { power: 'off' });
            return `Turned off ${device.name}`;
          } catch (error) {
            console.error(`Error turning off device ${device.id}:`, error);
            return `Error turning off ${device.name}`;
          }
        }
        
        if (lowerAction === 'set brightness' && value !== undefined && device.type === 'light') {
          await this.updateDevice(device.id, { brightness: value });
          return `Set ${device.name} brightness to ${value}%`;
        }
        
        if (lowerAction === 'set temperature' && value !== undefined && device.type === 'thermostat') {
          await this.updateDevice(device.id, { targetTemperature: value });
          return `Set ${device.name} temperature to ${value}°F`;
        }
        
        if (lowerAction === 'set speed' && value !== undefined && device.type === 'fan') {
          await this.updateDevice(device.id, { speed: value });
          return `Set ${device.name} speed to ${value}`;
        }
        
        if (lowerAction === 'lock' && device.type === 'lock') {
          await this.updateDevice(device.id, { locked: true });
          return `Locked ${device.name}`;
        }
        
        if (lowerAction === 'unlock' && device.type === 'lock') {
          await this.updateDevice(device.id, { locked: false });
          return `Unlocked ${device.name}`;
        }
        
        return `Unsupported action "${action}" for ${device.name}`;
      }));
      
      // Force a refresh of devices after processing commands
      setTimeout(async () => {
        try {
          await this.loadDevices();
        } catch (refreshError) {
          console.error('Error refreshing devices after command:', refreshError);
        }
      }, 500);
      
      return {
        success: true,
        message: results.join('. ')
      };
    } catch (error) {
      console.error('Error processing command:', error);
      return {
        success: false,
        message: 'Error processing command'
      };
    }
  }
  
  /**
   * Clean up resources when the service is no longer needed
   */
  cleanup(): void {
    ApiService.removeMessageHandler(this.handleDeviceUpdate);
    ApiService.disconnectWebSocket();
    this.isConnected = false;
  }
}

export default new DeviceService();